/**
 * AuscultationMap — mapa corporal con zonas clínicas clickeables.
 *
 * El alumno ve una silueta del paciente con 8 zonas estándar de auscultación
 * (4 focos cardíacos + 4 campos pulmonares). Al clickear una zona, la IA
 * genera el hallazgo realista según el caso, lo habla con TTS, y lo registra
 * en la bitácora del box.
 *
 * Zonas estándar:
 *   Cardíacas:
 *     - Aórtico: 2º EIC paraesternal derecho
 *     - Pulmonar: 2º EIC paraesternal izquierdo
 *     - Tricúspideo: 4º EIC paraesternal izquierdo
 *     - Mitral: 5º EIC línea medioclavicular izq (ápex)
 *   Pulmonares:
 *     - Vértice derecho / izquierdo
 *     - Base derecha / izquierda
 */

export type AuscZone =
  | 'cardio-aortic' | 'cardio-pulmonar' | 'cardio-tricuspideo' | 'cardio-mitral'
  | 'lung-apex-r' | 'lung-apex-l' | 'lung-base-r' | 'lung-base-l';

export interface AuscZoneDef {
  id: AuscZone;
  label: string;
  short: string;
  category: 'cardiac' | 'pulmonary';
  /** Posición en % sobre la silueta (0-100). */
  x: number;
  y: number;
}

export const AUSC_ZONES: AuscZoneDef[] = [
  { id: 'cardio-aortic',       label: 'Foco aórtico (2º EIC der)',    short: 'Ao',  category: 'cardiac',   x: 54, y: 35 },
  { id: 'cardio-pulmonar',     label: 'Foco pulmonar (2º EIC izq)',   short: 'Pu',  category: 'cardiac',   x: 46, y: 35 },
  { id: 'cardio-tricuspideo',  label: 'Foco tricuspídeo (4º EIC izq)', short: 'Tr', category: 'cardiac',   x: 47, y: 45 },
  { id: 'cardio-mitral',       label: 'Foco mitral (ápex, 5º EIC LMC)', short: 'Mi', category: 'cardiac',  x: 43, y: 50 },
  { id: 'lung-apex-r',         label: 'Vértice pulmonar derecho',     short: 'VPD', category: 'pulmonary', x: 58, y: 28 },
  { id: 'lung-apex-l',         label: 'Vértice pulmonar izquierdo',   short: 'VPI', category: 'pulmonary', x: 42, y: 28 },
  { id: 'lung-base-r',         label: 'Base pulmonar derecha',        short: 'BPD', category: 'pulmonary', x: 58, y: 55 },
  { id: 'lung-base-l',         label: 'Base pulmonar izquierda',      short: 'BPI', category: 'pulmonary', x: 42, y: 55 },
];

/**
 * Genera un hallazgo contextual según el caso y la zona. Es un fallback
 * local para cuando la IA no está disponible. La IA sobrescribe esto con
 * mayor riqueza clínica.
 */
export function localFindingFor(zone: AuscZone, diagnosis: string | undefined): string {
  const dx = (diagnosis || '').toLowerCase();
  const isCardiac = zone.startsWith('cardio-');
  const isLungRight = zone.endsWith('-r');
  const isApex = zone.includes('apex');

  if (isCardiac) {
    if (dx.includes('stemi') || dx.includes('infarto')) {
      if (zone === 'cardio-mitral') return 'R1 y R2 presentes, ruido regular. Sin 3er ruido. Sin soplos audibles en ápex.';
      return 'R1/R2 presentes, ritmo regular, sin soplos ni frotes en esta zona.';
    }
    if (dx.includes('sepsis') || dx.includes('shock')) {
      return 'Ruidos cardíacos apagados, ritmo regular taquicárdico. Sin soplos audibles.';
    }
    if (dx.includes('insuficiencia card') || dx.includes('ic')) {
      if (zone === 'cardio-mitral') return 'Soplo sistólico II/VI en ápex, irradiado a axila. R3 audible.';
      return 'R1/R2 presentes, taquicardia regular. Sin soplos en esta zona.';
    }
    return 'Ruidos cardíacos regulares, sin soplos audibles.';
  }

  // Pulmonar
  if (dx.includes('epoc') || dx.includes('asma')) {
    if (isApex) return 'Murmullo pulmonar conservado con sibilancias espiratorias difusas.';
    return 'Sibilancias espiratorias prolongadas bilaterales. Espiración alargada. Murmullo disminuido globalmente.';
  }
  if (dx.includes('neumon')) {
    if (!isApex && isLungRight) return 'Crépitos finos en base derecha. Soplo tubárico localizado. Egofonía positiva.';
    if (!isApex) return 'Murmullo pulmonar disminuido en base izquierda. Sin crépitos claros.';
    return 'Murmullo pulmonar conservado en vértices, sin ruidos agregados.';
  }
  if (dx.includes('edema') || dx.includes('ic') || dx.includes('insuficiencia card')) {
    if (!isApex) return 'Crépitos finos bilaterales en bases. Murmullo disminuido.';
    return 'Murmullo pulmonar conservado con algunos crépitos teleinspiratorios.';
  }
  if (dx.includes('tep')) {
    return 'Murmullo pulmonar conservado bilateralmente. Sin ruidos agregados. Puede haber frote pleural localizado.';
  }
  return 'Murmullo pulmonar vesicular conservado. Sin ruidos agregados.';
}
